import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    Site,
    SiteService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { preHide } from '@flyfreely-portal-ui/ngx-bootstrap-customisation';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const missionOverlapOptions = [
    { value: 'INFORM', name: 'Inform' },
    { value: 'ACKNOWLEDGE', name: 'Acknowledge' }
];

@Component({
    selector: 'site-edit-dialogue',
    templateUrl: './site-edit-dialogue.component.html'
})
export class SiteEditDialogue implements OnInit, OnDestroy {
    @Input() organisationId?: number;
    @Input() managingOrganisationId?: number;
    @Input() site?: Site | null;

    title: string;
    form: FormGroup;
    options: FormlyFormOptions;
    fields: FormlyFieldConfig[];
    model: Partial<Site>;

    private workTracker = new WorkTracker();
    working = false;
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private modal: BsModalRef<SiteEditDialogue>,
        modalOptions: ModalOptions,
        private siteService: SiteService,
        private logging: FlyFreelyLoggingService,
        private commonDialoguesService: CommonDialoguesService
    ) {
        this.form = new FormGroup({});
        modalOptions.closeInterceptor = () =>
            preHide(this.form, this.commonDialoguesService);
    }

    ngOnInit() {
        this.title =
            this.site?.id != null ? `Edit Site ${this.site.name}` : 'Add Site';

        this.model = {
            name: this.site?.name ?? undefined,
            description: this.site?.description ?? undefined,
            missionOverlapOperatingRequirement:
                this.site?.missionOverlapOperatingRequirement ?? undefined
        };

        this.fields = [
            {
                key: 'name',
                type: 'input',
                focus: true,
                props: {
                    label: 'Name',
                    required: true,
                    placeholder: 'Enter site name'
                }
            },
            {
                key: 'description',
                type: 'textarea',
                props: {
                    label: 'Description',
                    placeholder: 'Enter site description (optional)',
                    rows: 3
                }
            },
            {
                key: 'missionOverlapOperatingRequirement',
                type: 'ng-select',
                props: {
                    label: 'Mission Overlap Operating Requirement',
                    valueProp: 'value',
                    labelProp: 'name',
                    options: missionOverlapOptions,
                    required: true
                }
            }
        ];

        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    onSubmit() {
        if (this.form.invalid) {
            return;
        }
        const { name, description, missionOverlapOperatingRequirement } =
            this.form.value;

        const save$ =
            this.site?.id != null
                ? this.siteService.updateSite({
                      id: this.site.id,
                      managingOrganisationId: this.managingOrganisationId!,
                      name,
                      description,
                      missionOverlapOperatingRequirement
                  })
                : this.siteService.createSite({
                      organisationId: this.organisationId!,
                      managingOrganisationId: this.managingOrganisationId!,
                      name,
                      description,
                      missionOverlapOperatingRequirement
                  });

        save$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () => {
                    this.logging.success(
                        this.site?.id != null
                            ? 'Site has been updated'
                            : 'Site has been created'
                    );
                    this.form.markAsPristine();
                    this.modal.hide();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        this.site?.id != null
                            ? `Error updating site: ${error.message}`
                            : `Error creating site: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    hide() {
        this.modal.hide();
    }
}
